import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Award, Star, Sparkles, MapPin } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import StatsCounter from "@/components/site/StatsCounter";
import { AWARDS, AWARD_STATS } from "@/lib/data";

export default function Awards() {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...Array.from(new Set(AWARDS.map((a) => a.category)))];

  const filtered = useMemo(
    () => (filter === "All" ? AWARDS : AWARDS.filter((a) => a.category === filter)),
    [filter]
  );

  // Group by year for the timeline view
  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach((a) => {
      const arr = m.get(a.year) || [];
      arr.push(a);
      m.set(a.year, arr);
    });
    return Array.from(m.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [filtered]);

  return (
    <div data-testid="awards-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?w=2000"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Awards & Achievements</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            A decade of <span className="phoenix-text">recognition.</span>
          </h1>
          <p className="mt-5 text-base lg:text-lg text-white/70 max-w-2xl">
            From regional honours in Chennai to international stages and student crew championships — every Phoenix award is a shared victory.
          </p>
        </div>
      </section>

      <StatsCounter stats={AWARD_STATS} />

      {/* Awards listing */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="Honours"
          title="Awards &"
          accent="prizes."
          subtitle="A growing list of accolades earned by Phoenix as an institution, by our founders, and by our students across India, USA, and Canada."
          testid="awards-heading"
        />

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`awards-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-5 h-10 text-xs uppercase tracking-wider font-semibold border transition-colors ${
                filter === c
                  ? "bg-[#ff4500] border-[#ff4500] text-white"
                  : "border-white/15 text-white/70 hover:border-[#ffb700] hover:text-[#ffb700]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Awards grouped by year */}
        <div className="space-y-12">
          {grouped.map(([year, items]) => (
            <div key={year} data-testid={`awards-year-${year}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-3">
                <p className="font-display text-7xl lg:text-8xl phoenix-text leading-none">{year}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-3">
                  {items.length} {items.length === 1 ? "honour" : "honours"}
                </p>
              </div>
              <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((a, i) => (
                  <div
                    key={`${year}-${i}`}
                    data-testid={`award-${year}-${i}`}
                    className="group bg-[#0a0a0a] border border-[#ff4500]/40 hover:border-[#ff4500] p-7 transition-colors flex flex-col"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[#ff4500]/10 border border-[#ff4500]/40 group-hover:bg-[#ff4500] transition-colors">
                        <Trophy className="w-5 h-5 text-[#ff4500] group-hover:text-white transition-colors" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#ffb700]">{a.category}</span>
                        <h3 className="font-display text-xl lg:text-2xl text-white mt-1 leading-tight">{a.title}</h3>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-white/65 leading-relaxed flex-1">{a.description}</p>
                    <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-3 text-xs">
                      <span className="text-white/55 flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-[#ffb700]" strokeWidth={1.5} /> {a.org}
                      </span>
                      <span className="text-white/45 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {a.branch}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="p-12 text-center text-white/50 border border-dashed border-white/15">
            No awards in this category yet.
          </div>
        )}
      </section>

      {/* Featured trophy showcase */}
      <section className="py-20 lg:py-28 ember-bg border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="Spotlight" title="Most-watched" accent="moments." align="center" testid="spotlight-heading" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Trophy,
                title: "USA Got Talent 2026 — Title Winner",
                org: "Judges: Kala Master & Meena (Actress)",
                detail: "Phoenix USA crowned the 2026 USA Got Talent champions",
              },
              {
                icon: Star,
                title: "Sargolsav Title Winner 2026",
                org: "Sargolsav Dance Competition",
                detail: "Phoenix USA team takes the 2026 title",
              },
              {
                icon: Sparkles,
                title: "Star Performer — FETNA 2024",
                org: "Federation of Tamil Sangams of North America",
                detail: "Manikandan Gopinath honoured at FETNA's 36th Annual Convention",
              },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <div
                  key={i}
                  data-testid={`spotlight-${i}`}
                  className="bg-[#0a0a0a] border border-[#ff4500]/50 p-8 card-hover text-center"
                >
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#ff4500] mb-5">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-white">{t.title}</h3>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mt-2">{t.org}</p>
                  <p className="text-sm text-white/60 mt-4">{t.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-3">◆ Be part of the next chapter</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Train where champions are made.
            </h3>
          </div>
          <Link to="/book-trial">
            <button
              data-testid="awards-cta-btn"
              className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold whitespace-nowrap"
            >
              Book Free Trial
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
